import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';

import { PageHeader } from '@shared/components/PageHeader';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField, FormService } from '@shared/services/FormServices';
import { FIELD_CLASSES } from '@shared/utils/constants';

import { TimeOfUse } from '../create/RateConfiguration/RateTypes/TimeOfUse';
import { FlatRate } from '../create/RateConfiguration/RateTypes/FlatRate';
import { SlabBased } from '../create/RateConfiguration/RateTypes/SlabBased';
import { Seasonal } from '../create/RateConfiguration/RateTypes/Seasonal';
import { FixedRate } from '../create/RateConfiguration/RateTypes/FixedRate';
import { useRateDetail, useUpdateRate, useUtilityServices } from '../../hooks';
import { Button } from '@shared/ui/button';

export const RateEdit: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const {id} = useParams();
  const location = useLocation();
  const { editOption, scheduleDate } = location.state || {};
  const {data: rateDetail} = useRateDetail(id);
  const { mutate: updateRate ,isPending} = useUpdateRate(id);

  const currentVersion = `version-${parseInt(String(rateDetail?.version || "version-1").replace(/\D/g, ''), 10) || 1}`;
  const nextVersion = `version-${(parseInt(String(rateDetail?.version || "version-1").replace(/\D/g, ''), 10) || 1) + 1}`;

  const rateComponentRef = useRef<any>(null);

  const handleSave = async () => {
    const isValid = await basicInfoForm.trigger();
    if (!isValid) return;

    const dirtyFields = basicInfoForm.formState.dirtyFields;
    const allValues = basicInfoForm.getValues();
    
    const editedFields = Object.keys(dirtyFields).reduce((acc: any, key) => {
      acc[key] = allValues[key];
      return acc;
    }, {});    

    const payload: any = {};
    if ('rateName' in editedFields) payload.rate_name = editedFields.rateName;
    if ('rateCode' in editedFields) payload.rate_code = editedFields.rateCode;
    if ('description' in editedFields) payload.description = editedFields.description;
    if ('utilityService' in editedFields) payload.utility_service = editedFields.utilityService;
    if ('effectiveFrom' in editedFields) payload.effective_date = editedFields.effectiveFrom;
    if ('endDate' in editedFields) payload.expiry_date = editedFields.endDate;

    if (editOption === 'schedule' && scheduleDate) {
      payload.start_date = scheduleDate;
    }

    if (rateComponentRef.current) {
      const rateConfig = await rateComponentRef.current.submit();
      
      if (!rateConfig) {
        return; // Validation failed in the child component
      }

      const normalize = (key: string, value: any) => {
        if (key === 'id') return undefined;
        if (value === null || value === "") return undefined;
        if (typeof value === 'number') return String(value);
        
        // Sort object keys deterministically
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return Object.keys(value).sort().reduce((sorted: any, k: string) => {
            sorted[k] = value[k];
            return sorted;
          }, {});
        }
        return value;
      };

      // Determine if rate structure changed by deep comparing with initial configuration
      const isRateDirty = JSON.stringify(rateConfig, normalize) !== JSON.stringify(initialRateConfig, normalize);
      
      if (isRateDirty && rateConfig) {
        const rtStr = rateDetail?.rateType?.toString();
        
        if (rtStr === "3" || rtStr === "Flat Rate") {
          payload.rate = [{ rate: Number(rateConfig.rate) || 0, unit: rateConfig.unit || "" }];
        } else if (rtStr === "4" || rtStr === "Slab Based") {
          const slabs = rateConfig.slabs || [];
          payload.rate = slabs.map((s: any) => ({
            from: Number(s.from) || 0,
            to: s.to && s.to !== "" && s.to !== "∞" ? Number(s.to) : null,
            mode: s.type || "variable",
            rate: Number(s.rate) || 0,
            unit: s.unit || ""
          }));
          payload.min_charge_enabled = false;
          payload.min_charge_threshold = 0;
          payload.min_charge_rate = 0.00;
        } else if (rtStr === "5" || rtStr === "Time of Use") {
          const slots = rateConfig.slots || [];
          payload.rate = slots.map((s: any) => {
            const parseTimeStr = (t: string) => {
              if (!t) return "00:00";
              if (t.includes(':')) return t;
              return `${String(Number(t) || 0).padStart(2, '0')}:00`;
            };
            return {
              name: s.slotName || "",
              start_time: parseTimeStr(s.from),
              end_time: parseTimeStr(s.to),
              rate: Number(s.rate) || 0,
              unit: s.unit || "",
              color: s.color || ""
            };
          });
        } else if (rtStr === "6" || rtStr === "Seasonal") {
          const seasons = rateConfig.seasons || [];
          payload.rate= seasons.map((s: any) => {
            const seasonRtStr = s.rateType?.toString().toLowerCase();
            let mappedType = 0;
            let structure = [];
            
            if (seasonRtStr === "slab based" || seasonRtStr === "4") {
              mappedType = 4;
              structure = (s.rateConfig?.slabs || []).map((slab: any) => ({
                from: Number(slab.from) || 0,
                to: slab.to && slab.to !== "" && slab.to !== "∞" ? Number(slab.to) : null,
                mode: slab.type || "variable",
                rate: Number(slab.rate) || 0,
                unit: slab.unit || ""
              }));
            } else if (seasonRtStr === "flat rate" || seasonRtStr === "3") {
              mappedType = 3;
              structure = [{ rate: Number(s.rateConfig?.rate) || 0, unit: s.rateConfig?.unit || "" }];
            } else if (seasonRtStr === "fixed" || seasonRtStr === "7") {
              mappedType = 7;
              structure = [{ fixed: Number(s.rateConfig?.rate) || 0, variable: 0, unit: s.rateConfig?.unit || "" }];
            } else if (seasonRtStr === "time of use" || seasonRtStr === "5") {
              mappedType = 5;
              structure = (s.rateConfig?.slots || []).map((slot: any) => {
                const parseTimeStr = (t: string) => {
                  if (!t) return "00:00";
                  if (t.includes(':')) return t;
                  return `${String(Number(t) || 0).padStart(2, '0')}:00`;
                };
                return {
                  name: slot.slotName || "",
                  start_time: parseTimeStr(slot.from),
                  end_time: parseTimeStr(slot.to),
                  rate: Number(slot.rate) || 0,
                  unit: slot.unit || "",
                  color: slot.color || ""
                };
              });
            }

            return {
              name: s.seasonName || "",
              start_date: s.from || "",
              end_date: s.to || "",
              rate_type: mappedType,
              structure
            };
          });
        } else if (rtStr === "7" || rtStr === "Fixed") {
          payload.rate= {
            fixed: Number(rateConfig.rate) || 0,
            variable: 0,
            unit: rateConfig.unit || ""
          };
        }
      }
    }

    if (Object.keys(payload).length > 0) {
      updateRate(payload, {
        onSuccess: () => {
          navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff", { replace: true });
        }
      });
    } else {
      navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff", { replace: true });
    }
  };

  const handleCancel = () => {
    navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff", { replace: true });
  };

  const {data: utilityServices} = useUtilityServices();

  const basicInfoFields: FormField[] = useMemo(
    () => [
      {
        name: "rateName",
        label: "Rate Name",
        type: "text",
        required: true,
        placeholder: "e.g. Water - Residential Tiered",
        helperText: "Customer-facing name. Appears on bills, statements, and the consumer portal.",
        classes: FIELD_CLASSES,
      },
      {
        name: "rateCode",
        label: "Rate Code",
        type: "text",
        required: true,
        placeholder: "WTR-RES-T1",
        helperText: "Used in billing files and APIs. Format: SERVICE-CATEGORY-VARIANT.",
        classes: FIELD_CLASSES,
      },
      {
        name: "utilityService",
        label: "Utility Service",
        type: "select",
        required: true,
        isSearchable: false,
        placeholder: "Select Utility Service...",
        options: utilityServices?.map((u: any) => ({
            label: u.name,
            value: u.name,
          })) || [],
        helperText: "The service this tariff bills for. Drives meter type, billing units, and rate structure.",
        classes: FIELD_CLASSES,
      },
      {
        name: "effectiveFrom",
        label: "Effective From",
        type: "datepicker",
        required: true,
        placeholder: "Select effective date",
        helperText: "When this tariff begins applying to bills.",
        classes: FIELD_CLASSES,
      },
      {
        name: "endDate",
        label: "Effective To",
        type: "datepicker",
        placeholder: "Select effective date",
        helperText: "Leave blank for open-ended.",
        classes: FIELD_CLASSES,
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Internal description and intent of this tariff. Visible to rate analyst and approvers.",
        helperText: "Internal notes for rate analysts and approvers.",
        classes: FIELD_CLASSES,
        className: "md:col-span-2"
      }
    ],
    [utilityServices]
  );

  const basicInfoForm = FormService.createForm({
    fields: basicInfoFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: rateDetail,
  });

  const selectedUtilityServiceName = basicInfoForm.watch("utilityService");
  useEffect(()=>{
    if(rateDetail){
      FormService.populateForm(basicInfoForm, {
        rateName:rateDetail?.rateName, 
        rateCode:rateDetail?.rateCode, //missing in api
        utilityService:rateDetail?.utilityService,
        effectiveFrom:rateDetail?.effectiveDate,
        endDate:rateDetail?.expiryDate,
        description:rateDetail?.description
      },basicInfoFields);
    }
  },[rateDetail])

  const initialRateConfig = useMemo(() => {
    let rateType = rateDetail?.rateType;
     if(rateDetail?.basisType === "Flat"){
      rateType = 3;
    }else if(rateDetail?.basisType === "Time Of Use"){
      rateType = 5;
    }else if(rateDetail?.basisType === "Slab" || rateDetail?.basisType === "Tiered"){
      rateType = 4;
    }else if(rateDetail?.basisType === "Fixed"){
      rateType = 7;
    }
    let rateConfig: any = rateDetail?.rate;

    const formatTime = (timeVal: any) => {
      if (timeVal == null || timeVal === "") return "";
      
      if (typeof timeVal === 'number' || (typeof timeVal === 'string' && !timeVal.includes(':'))) {
        const numVal = Number(timeVal);
        if (!isNaN(numVal)) {
          const hours = Math.floor(numVal);
          const minutes = Math.round((numVal - hours) * 60);
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
      }

      if (typeof timeVal === 'string' && timeVal.includes(':')) {
        const parts = timeVal.split(':');
        return `${parts[0].padStart(2, '0')}:${(parts[1] || '00').padStart(2, '0')}`;
      }

      return timeVal || "";
    };

    const parseTime = (timeStr: string) => {
      if (!timeStr) return "00:00";
      if (timeStr.includes(':')) {
        return timeStr; // Return 'HH:mm' as requested by the backend
      }
      // Fallback for older integer data
      const numVal = Number(timeStr) || 0;
      const hours = Math.floor(numVal);
      const minutes = Math.round((numVal - hours) * 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    if (rateConfig) {
      const isArray = Array.isArray(rateConfig);
      switch (rateType) {
        case 3:{
          rateConfig = isArray ? rateConfig[0] || {} : rateConfig;
          break;
        }
        case 7: {
          const config = isArray ? rateConfig[0] || {} : rateConfig;
          rateConfig = {
            unit: config.unit,
            rate: config.fixed !== undefined ? config.fixed : config.rate
          };
          break;
        }
        case 6: {
          const seasonsArray = isArray ? rateConfig : (rateConfig.seasons || []);
          const mappedSeasons = seasonsArray.map((item: any) => {
            let mappedRateType = "Flat Rate";
            switch (item.rateType) {
              case 3: mappedRateType = "Flat Rate"; break;
              case 4: mappedRateType = "Slab Based"; break;
              case 5: mappedRateType = "Time of Use"; break;
              case 7: mappedRateType = "Fixed"; break;
            }
            
            let childConfig: any = {};
            switch (mappedRateType) {
              case "Flat Rate":
                childConfig = item.structure?.[0] || {};
                break;
              case "Fixed": {
                const config = item.structure?.[0] || {};
                childConfig = {
                  unit: config.unit,
                  rate: config.fixed !== undefined ? config.fixed : config.rate
                };
                break;
              }
              case "Slab Based":
                childConfig = {
                  slabs: (item.structure || []).map((s: any) => ({
                    from: s.from,
                    to: s.to,
                    type: s.mode || s.type || "variable",
                    rate: s.rate,
                    unit: s.unit
                  }))
                };
                break;
              case "Time of Use":
                childConfig = {
                  slots: (item.structure || []).map((s: any) => ({
                    color: s.color || "#3b82f6",
                    slotName: s.slotName || s.name || "",
                    from: formatTime(s.from ?? s.startTime ?? s.start_time),
                    to: formatTime(s.to ?? s.endTime ?? s.end_time),
                    rate: s.rate,
                    unit: s.unit
                  }))
                };
                break;
            }

            return {
              seasonName: item.name || item.seasonName,
              from: item.startDate || item.from,
              to: item.endDate || item.to,
              rateType: mappedRateType,
              rateConfig: childConfig
            };
          });
          rateConfig = { seasons: mappedSeasons };
          break;
        }
        case 5: {
          const slotsArray = isArray ? rateConfig : (rateConfig.slots || []);
          const mappedSlots = slotsArray.map((item: any) => ({
            color: item.color || "#3b82f6",
            slotName: item.name || item.slotName || "",
            from: formatTime(item.startTime ?? item.start_time),
            to: formatTime(item.endTime ?? item.end_time),
            rate: item.rate,
            unit: item.unit
          }));
          rateConfig = { slots: mappedSlots };
          break;
        }
        case 4: {
          const slabsArray = isArray ? rateConfig : (rateConfig.slabs || []);
          const mappedSlabs = slabsArray.map((item: any) => ({
            from: item.from ?? item.startTime ?? 0,
            to: item.to ?? item.endTime ?? 0,
            type: item.mode || item.type || "variable",
            rate: item.rate,
            unit: item.unit
          }));
          rateConfig = { slabs: mappedSlabs };
          break;
        }
      }
    }
    return rateConfig;
  }, [rateDetail]);

  const renderRateComponent = () => {
    let rateType = rateDetail?.rateType ;
    console.log('basis type', rateDetail?.basisType);

    if(rateDetail?.basisType === "Flat"){
      rateType = 3;
    }else if(rateDetail?.basisType === "Time Of Use"){
      rateType = 5;
    }else if(rateDetail?.basisType === "Slab" || rateDetail?.basisType === "Tiered"){
      rateType = 4;
    }else if(rateDetail?.basisType === "Fixed"){
      rateType = 7;
    }
    const rateConfig = initialRateConfig;

    const selectedUtilityService = utilityServices?.find((u: any) => u.name === selectedUtilityServiceName);
    const serviceUnit = selectedUtilityService?.unit || "";

    switch (rateType) {
      case 5:
        return <TimeOfUse ref={rateComponentRef} data={rateConfig} hideHeader={false} unit={serviceUnit}/>;
      case 3:
        return <FlatRate ref={rateComponentRef} data={rateConfig} hideHeader={false} unit={serviceUnit}/>;
      case 4:
        return <SlabBased ref={rateComponentRef} data={rateConfig} hideHeader={false} unit={serviceUnit}/>;
      case 6:
        return <Seasonal ref={rateComponentRef} data={rateConfig} unit={serviceUnit}/>;
      case 7:
        return <FixedRate ref={rateComponentRef} data={rateConfig} hideHeader={false} unit={serviceUnit}/>;
      default:
        return (
          <div className="p-8 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
            Configuration for <strong>{rateType}</strong> is coming soon.
          </div>
        );
    }
  };

  return (
    <div>
      <PageHeader 
        title='Edit Rate' 
        subtitle='Modify the configuration for this rate. Changes will create a new version to protect historical bills.'
        icon={ArrowLeft}
        onIconClick={handleCancel}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-4">
        {/* Left Column - Forms */}
        <div className="lg:col-span-3 space-y-6">
          
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Basic Information</h3>
            <p className="text-sm text-gray-600 mb-6">Provide identifying details for this tariff.</p>
            <DynamicForm
              fields={basicInfoFields}
              form={basicInfoForm}
              config={{
                showSubmitButton: false,
                showResetButton: false,
              }}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2 }}
            />
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Rate Configuration: {rateDetail?.rateTypeDisplay}</h3>
            <p className="text-sm text-gray-600 mb-6">Configure parameters for this rate structure.</p>
            {renderRateComponent()}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
            <Button 
              onClick={handleCancel}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              isLoading={isPending}
            >
              Save Changes
            </Button>
          </div>
        </div>

        {/* Right Column - Version Impact */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">Version impact</h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Current</div>
                  <div className="text-lg font-bold text-gray-900">
                    {currentVersion}
                  </div>
                </div>
                <div className="text-gray-400 mt-5 text-lg font-medium">→</div>
                <div className="text-right">
                  <div className="text-sm text-gray-500 mb-1">New</div>
                  <div className="text-lg font-bold text-gray-900">
                    {nextVersion}
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg flex items-start gap-2 border border-blue-100 mt-2">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800 space-y-1">
                  <p>Historical bills protected. Bills generated under {currentVersion} keep their original rates.</p>
                  <p>Saving will create {nextVersion} as the new active version.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">Current state</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">Status</div>
                <div className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">{rateDetail?.status}</div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">Effective</div>
                <div className="text-sm font-medium text-gray-700">{rateDetail?.effectiveDate}</div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">End</div>
                <div className="text-sm font-medium text-gray-700">{rateDetail?.expiryDate ?? 'NA'}</div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
